<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <v-card
      class="pa-8"
    >
      <div>
        <div class="d-flex align-center justify-center">
          <div class="text-h4 font-weight-medium">
            🧮 {{ $t('CostAccounting') }}
          </div>
        </div>

        <template v-if="!saveLoading">
          <loading-provider :loading="loading">
            <div
              class="mt-8"
              style="display: grid;
              grid-template-columns: repeat(2,minmax(0,1fr));grid-gap: 24px"
            >
              <v-card
                class="pa-2 py-4"
                color="transparent"
                elevation="0"
                height="min-content"
              >
                <div>
                  <div class="d-flex align-start">
                    <div class="text-h5">
                      {{ $t('RawMaterialCosts') }}
                    </div>
                    <v-spacer />
                    <v-btn
                      v-if="!inventoryIsActive"
                      class="blue lighten-4"
                      color="black--text"
                      elevation="0"
                      small
                      @click="addNewItem(1)"
                    >
                      {{ $t('NewItem') }}
                    </v-btn>
                  </div>
                </div>

                <div
                  class="mt-4"
                >
                  <template v-if="!inventoryIsActive">
                    <div
                      v-for="item in realResourceCostList"
                      :key="item.label"
                    >
                      <v-card
                        class="pa-2 my-2"
                        color="grey lighten-4"
                        elevation="0"
                        outlined
                        @click="editCostItem(1,item,1)"
                      >
                        <div class="d-flex align-center justify-center">
                          {{ item.label }}
                          <v-spacer />
                          {{ item.value | reportPriceDisplay }}
                        </div>
                      </v-card>
                    </div>
                    <div
                      v-for="item in addResourceCostItem"
                      :key="item.label"
                    >
                      <v-card
                        class="pa-2 my-2"
                        color="grey lighten-4"
                        elevation="0"
                        outlined
                        @click="editCostItem(1,item,2)"
                      >
                        <div class="d-flex align-center justify-center">
                          {{ item.label }}
                          <v-spacer />
                          {{ item.value | reportPriceDisplay }}
                        </div>
                      </v-card>
                    </div>
                  </template>
                  <template v-else>
                    <div class="mt-8">
                      <div class="text-h4">
                        {{ $t('DefaultCostsCalcText') }}
                      </div>
                    </div>
                  </template>
                </div>
              </v-card>
              <v-card
                class="pa-2 py-4"
                color="transparent"
                elevation="0"
                height="min-content"
              >
                <div>
                  <div class="d-flex align-start">
                    <div class="text-h5">
                      {{ $t('FixedCosts') }}
                    </div>
                    <v-spacer />
                    <v-btn
                      class="blue lighten-4"
                      color="black--text"
                      elevation="0"
                      small
                      @click="addNewItem(2)"
                    >
                      {{ $t('NewItem') }}
                    </v-btn>
                  </div>
                </div>
                <div
                  class="mt-4"
                >
                  <div
                    v-for="item in realFixedCostList"
                    :key="item.label"
                  >
                    <v-card
                      class="pa-2 my-2"
                      color="grey lighten-4"
                      elevation="0"
                      outlined
                      @click="editCostItem(2,item,1)"
                    >
                      <div class="d-flex align-center justify-center">
                        {{ item.label }}
                        <v-spacer />
                        {{ item.value | reportPriceDisplay }}
                      </div>
                    </v-card>
                  </div>
                  <div
                    v-for="item in addFixedCostItem"
                    :key="item.label"
                  >
                    <v-card
                      class="pa-2 my-2"
                      color="grey lighten-4"
                      elevation="0"
                      outlined
                      @click="editCostItem(2,item,2)"
                    >
                      <div class="d-flex align-center justify-center">
                        {{ item.label }}
                        <v-spacer />
                        {{ item.value | reportPriceDisplay }}
                      </div>
                    </v-card>
                  </div>
                </div>
              </v-card>
            </div>
          </loading-provider>
        </template>
        <template v-else>
          <div class="d-flex align-center justify-center my-16">
            <v-progress-circular
              indeterminate
              size="64"
            />
          </div>
        </template>
      </div>
    </v-card>
    <v-dialog
      v-model="newItemDialog"
      max-width="400"
    >
      <v-card class="pa-4">
        <div class="d-flex align-center mb-4">
          <div class="text-h4 font-weight-bold">
            {{ editItem ? $t('edit') : $t('add') }}
          </div>
          <v-spacer />
          <div>
            <v-btn
              icon
              @click="newItemDialog = false"
            >
              <v-icon large>
                mdi-close
              </v-icon>
            </v-btn>
          </div>
        </div>
        <div class="d-flex align-center justify-center flex-column">
          <v-form
            ref="form"
            v-model="valid"
            style="width: 100%"
          >
            <v-text-field
              v-model="newItemLabel"
              :disabled="itemType === 1"
              :label="addCostType === 1 ? $t('resourceName') : $t('ExpenseLabel')"
              :rules="rules"
              color="secondary"
              filled
            />
            <v-text-field
              v-model="newItemValue"
              :label="addCostType === 1 ? $t('RawMaterialCosts') : $t('FixedCosts')"
              :rules="rules"
              color="secondary"
              filled
              type="number"
            />
          </v-form>
          <template v-if="!editItem">
            <v-btn
              class="amber lighten-4 black--text"
              elevation="0"
              outlined
              width="100%"
              @click="pushIntoList"
            >
              {{ $t('confirm') }}
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              v-if="itemType !== 1"
              class="blue lighten-2 mb-2"
              elevation="0"
              outlined
              width="100%"
              @click="deleteItem"
            >
              {{ $t('delete') }}
            </v-btn>
            <v-btn
              class="amber lighten-4 black--text"
              elevation="0"
              outlined
              width="100%"
              @click="editList"
            >
              {{ $t('confirm') }}
            </v-btn>
          </template>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { dragscroll } from 'vue-dragscroll/src/main'
import {
  getFixedDefaultCostList,
  getFixedExtraCostList,
  getPurchasingDefaultCostList,
  getPurchasingExtraCostList,
  settingFixedDefaultCost,
  settingFixedExtraCost,
  settingPurchasingDefaultCost,
  settingPurchasingExtraCost
} from '@/model/dataLayer/api/report/salesReportApi'
import { checkCloudResourceTrack } from '@/model/dataLayer/api/storage/storage'
import { mapMutations } from 'vuex'
import LoadingProvider from '@/views/widget/LoadingProvider'

export default {
  name: 'DefaultCostPage',
  components: { LoadingProvider },
  directives: {
    LoadingProvider,
    dragscroll
  },
  props: {
    dialogStatus: {}
  },
  data: function () {
    return {
      saveLoading: false,
      loading: false,
      itemType: null,
      newItemValue: '',
      editItem: '',
      rules: [
        v => (!!v || v === 0) || this.$t('InputFormEmptyHint')
      ],
      valid: true,
      addCostType: null,
      selectType: true,
      newItemLabel: '',
      newItemDialog: false,
      inventoryIsActive: false,
      resourceCost: [
        {
          label: this.$t('GroceryShopping'),
          value: 0
        }, {
          label: this.$t('AlcoholicDrinks'),
          value: 0
        }, {
          label: this.$t('NonAlcoholicDrinks'),
          value: 0
        }, {
          label: this.$t('Consumables'),
          value: 0
        }, {
          label: this.$t('DryGoods'),
          value: 0
        }
      ],
      fixedCost: [
        {
          label: this.$t('FixedCostsEmployment'),
          value: 0
        }, {
          label: this.$t('FixedCostsEmploymentFlexible'),
          value: 0
        }, {
          label: this.$t('MonthlyRent'),
          value: 0
        }, {
          label: this.$t('WaterAndElectricityCosts'),
          value: 0
        }, {
          label: this.$t('EquipmentDepreciation'),
          value: 0
        }, {
          label: this.$t('LowValueConsumables'),
          value: 0
        }, {
          label: this.$t('MarketingCosts'),
          value: 0
        }
      ],
      resourceDefaultList: [],
      fixedDefaultList: [],
      addResourceCostItem: [],
      addFixedCostItem: []
    }
  },
  computed: {
    realResourceCostList () {
      return this.resourceDefaultList.length > 0 ? this.resourceDefaultList : this.resourceCost
    },
    realFixedCostList () {
      return this.fixedDefaultList.length > 0 ? this.fixedDefaultList : this.fixedCost
    }
  },
  watch: {
    dialogStatus (val) {
      if (val) {
        this.reload()
      }
    },
    async newItemDialog (val) {
      if (!val) {
        this.selectType = true
        this.addCostType = null
        this.newItemLabel = ''
        this.newItemValue = ''
        this.editItem = ''
        this.itemType = null
      }
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    ...mapMutations(['showErrorDialog']),
    editList () {
      this.editItem.label = this.newItemLabel
      this.editItem.value = this.newItemValue
      this.newItemDialog = false
      this.saveItem()
    },
    editCostItem (type, item, defaultItem) {
      this.itemType = defaultItem
      this.editItem = item
      this.addCostType = type
      this.newItemLabel = item.label
      this.newItemValue = item.value
      this.newItemDialog = true
    },
    async reload () {
      this.loading = true
      this.addResourceCostItem = (await getPurchasingExtraCostList())
      this.addFixedCostItem = (await getFixedExtraCostList())
      this.resourceDefaultList = (await getPurchasingDefaultCostList())
      this.fixedDefaultList = (await getFixedDefaultCostList())
      this.inventoryIsActive = await checkCloudResourceTrack()
      this.loading = false
    },
    deleteItem () {
      if (this.addCostType === 1) {
        this.addResourceCostItem = this.addResourceCostItem.filter(it => it.label !== this.newItemLabel)
      } else {
        this.addFixedCostItem = this.addFixedCostItem.filter(it => it.label !== this.newItemLabel)
      }
      this.saveItem()
      this.newItemDialog = false
    },
    pushIntoList () {
      if (this.addCostType === 1) {
        this.addResourceCostItem.push({
          label: this.newItemLabel,
          value: this.newItemValue
        })
      } else {
        if (!this.addFixedCostItem.find(it => it.label === this.newItemLabel)) {
          this.addFixedCostItem.push({
            label: this.newItemLabel,
            value: this.newItemValue
          })
        } else {
          this.showErrorDialog('不能添加名称相同的项目')
        }
      }
      this.saveItem()
      this.newItemDialog = false
    },
    addNewItem (type) {
      this.addCostType = type
      this.newItemDialog = true
    },
    async saveItem () {
      this.saveLoading = true
      await settingFixedExtraCost(this.addFixedCostItem)
      await settingPurchasingExtraCost(this.addResourceCostItem)
      await settingPurchasingDefaultCost(this.realResourceCostList)
      await settingFixedDefaultCost(this.realFixedCostList)
      this.saveLoading = false
      this.$emit('closeDialog')
    }
  }
}
</script>

<style scoped>

</style>
